/**
 * Accent palette + type→icon map, ported from `docs/architecture-map.html`
 * (the `TYPE_GLYPH` table, line ~411). This is the one sanctioned place for
 * literal hex in the graph — everything else themes via shadcn CSS vars.
 *
 * The UI accent is BLACK (feedback #1): the default accent and the formerly-
 * indigo AOS type entries are near-black `#111111`; the categorical status/type
 * hues (orange/green/violet/cyan/…) are retained for differentiation. The card
 * chip glyph is a HugeIcons icon resolved via {@link ENTITY_ICON}.
 */
import type { IconSvgElement } from "@hugeicons/react";
import {
  PackageIcon,
  SparklesIcon,
  Layers01Icon,
  Target02Icon,
  WorkflowSquare01Icon,
  RocketIcon,
  BookOpen01Icon,
  Idea01Icon,
  File01Icon,
  ChartLineData01Icon,
  CheckmarkCircle02Icon,
  Activity01Icon,
  DashboardSquare01Icon,
  ConnectIcon,
  ArrowRight01Icon,
  Settings01Icon,
  Atom01Icon,
  Share08Icon,
  CubeIcon,
  DistributionIcon,
  Database01Icon,
  GridIcon,
  Building01Icon,
  Notebook01Icon,
  Flag01Icon,
  Hexagon01Icon,
} from "@hugeicons/core-free-icons";

/** Near-black emphasis ink for active edge strokes + arrowhead marker defs.
 *  SVG `<marker>` defs cannot read CSS vars, so a literal is required here; this
 *  is the BLACK-accent equivalent the graph emphasis must use (feedback #1). */
export const EMPHASIS_INK = "#0f172a";

/** type → [accent color, glyph char] */
export const TYPE_GLYPH: Record<string, [string, string]> = {
  // architecture-map.html types (kept for parity / reuse)
  UI: ["#7c3aed", "◧"],
  Provider: ["#0d9488", "⇄"],
  Route: ["#2563eb", "⇥"],
  Middleware: ["#0891b2", "⚙"],
  Kernel: ["#ea580c", "◎"],
  Effect: ["#f59e0b", "↯"],
  Executor: ["#ea580c", "↗"],
  Connector: ["#c026d3", "⤳"],
  Service: ["#9333ea", "◆"],
  Queue: ["#0369a1", "≋"],
  Repo: ["#475569", "▤"],
  DB: ["#334155", "▦"],
  Schema: ["#0891b2", "∷"],
  Infra: ["#0f172a", "◉"],
  External: ["#b91c1c", "◆"],
  Content: ["#16a34a", "≡"],

  // AOS domain types (packages / contexts / processes)
  principle: ["#111111", "✦"],
  frame: ["#0891b2", "▤"],
  bet: ["#ea580c", "◆"],
  contour: ["#9333ea", "◎"],
  skill: ["#16a34a", "↗"],
  package: ["#111111", "▦"],

  context: ["#111111", "◧"],
  decision: ["#9333ea", "◆"],
  objective: ["#ea580c", "✦"],
  artifact: ["#0d9488", "≡"],
  metric: ["#0891b2", "∿"],
  evidence: ["#16a34a", "✓"],
  signal: ["#b91c1c", "↯"],

  // process graph node roles
  Start: ["#16a34a", "▶"],
  Phase: ["#111111", "◎"],
  Terminal: ["#475569", "■"],
};

/**
 * Central type → HugeIcons icon map for the card chip (feedback #3 / #8c). The
 * ONE place node glyphs are chosen; keyed by the same node `type` keys as
 * `TYPE_GLYPH`. Resolve via {@link glyphIconFor} (falls back to a hexagon).
 */
export const ENTITY_ICON: Record<string, IconSvgElement> = {
  // architecture-map.html types
  UI: DashboardSquare01Icon,
  Provider: ConnectIcon,
  Route: ArrowRight01Icon,
  Middleware: Settings01Icon,
  Kernel: Atom01Icon,
  Effect: Activity01Icon,
  Executor: RocketIcon,
  Connector: Share08Icon,
  Service: CubeIcon,
  Queue: DistributionIcon,
  Repo: Database01Icon,
  DB: Database01Icon,
  Schema: GridIcon,
  Infra: Building01Icon,
  External: Share08Icon,
  Content: Notebook01Icon,

  // AOS domain types
  principle: SparklesIcon,
  frame: Layers01Icon,
  bet: Target02Icon,
  contour: WorkflowSquare01Icon,
  skill: RocketIcon,
  package: PackageIcon,
  context: BookOpen01Icon,
  decision: Idea01Icon,
  objective: Target02Icon,
  artifact: File01Icon,
  metric: ChartLineData01Icon,
  evidence: CheckmarkCircle02Icon,
  signal: Activity01Icon,

  // process graph node roles
  Start: RocketIcon,
  Phase: WorkflowSquare01Icon,
  Terminal: Flag01Icon,
};

/** Resolve a HugeIcons icon for a node type; unknown types → a hexagon. */
export function glyphIconFor(type: string): IconSvgElement {
  return ENTITY_ICON[type] ?? Hexagon01Icon;
}

/** Deterministic per-cluster accent ring (cycled by first-appearance index).
 *  Neutral slate leads so a cluster never reads as the banned indigo accent. */
export const CLUSTER_ACCENTS = [
  "#334155",
  "#16a34a",
  "#ea580c",
  "#9333ea",
  "#0891b2",
  "#475569",
  "#b91c1c",
  "#0d9488",
];

/** Near-black — the BLACK UI accent (feedback #1), replacing the old indigo. */
export const DEFAULT_ACCENT = "#111111";

export function typeColor(type: string): string {
  return TYPE_GLYPH[type]?.[0] ?? DEFAULT_ACCENT;
}

export function glyphFor(type: string): string {
  return TYPE_GLYPH[type]?.[1] ?? "•";
}

/** Accent for a cluster key, by first-appearance order, cycled through the palette. */
export function clusterColor(
  cluster: string | undefined,
  order: string[],
): string | undefined {
  if (!cluster) return undefined;
  const idx = order.indexOf(cluster);
  if (idx < 0) return undefined;
  return CLUSTER_ACCENTS[idx % CLUSTER_ACCENTS.length];
}

/** Palette to auto-color flows that don't carry an explicit color. */
export const FLOW_PALETTE = CLUSTER_ACCENTS;
